<!-- La feuille descend : à pleine hauteur, l'écran se terminait par une grande
     surface blanche vide, faute d'assez de contenu pour la remplir. Le retrait
     haut suit la convention des autres feuilles du projet, en pourcentage. -->
<vw-toast-popup
  [isOpen]="isOpen"
  width="100%"
  borderRadius="20px"
  paddingTop="65%"
  customClass="bg-white"
  (close)="onClose()"
>
  <!-- Colonne pleine hauteur : la sortie s'ancre en bas, et le contenu défile
       quand il dépasse. Un `h-full` sans défilement coupait le bas de l'écran
       dès que l'état devenait bavard (paiement en attente, échéance…). -->
  <div class="flex h-full flex-col overflow-y-auto">
    @if (state?.isPremium) {
      <!-- L'état, en un bloc : de quoi il s'agit, ce qui reste, ce qui suit.
           Le vert profond est celui de la carte du profil et du paywall — un
           abonnement ne change pas d'identité selon l'écran. -->
      <div
        class="bg-gradient-to-br from-[#0B3A33] to-[#125046] px-5 pb-5 pr-16 pt-6 text-white"
      >
        <p class="text-[13px] text-white/60">
          {{ "premium.manage.title" | translate }}
        </p>

        <div class="mt-2 flex items-center gap-2.5">
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#F79400] to-[#F0C97A]"
          >
            <svg
              viewBox="0 0 24 24"
              class="h-5 w-5 text-white"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8L12 2z"
              />
            </svg>
          </span>
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-[#F0C97A]"
          >
            @if (isGifted) {
              {{
                (state?.provider === "TRIAL"
                  ? "premium.manage.kind-trial"
                  : "premium.manage.kind-gift"
                ) | translate
              }}
            } @else {
              {{ "premium.profile.member-badge" | translate }}
            }
          </p>
        </div>

        <!-- Le chiffre qui compte d'abord : les jours restants quand l'accès a
             une fin, le prix quand il se renouvelle. -->
        <p class="mt-3 text-[26px] font-semibold leading-tight">
          @if (isGifted && daysLeft !== null) {
            {{ "premium.manage.days-left" | translate: { count: daysLeft } }}
          } @else if (isCanceled && periodEndLabel) {
            {{ "premium.manage.ends-on" | translate: { date: periodEndLabel } }}
          } @else if (isGifted) {
            <!-- Un accès offert sans échéance connue : il n'y a ni compte à
                 rebours à afficher, ni prix qui aurait un sens ici. -->
            {{ "premium.profile.member-title" | translate }}
          } @else {
            {{
              (state?.plan === "YEARLY"
                ? "premium.paywall.yearly"
                : "premium.paywall.monthly"
              ) | translate
            }}
          }
        </p>

        <p class="mt-0.5 text-sm text-white/80">
          @if (isCanceled) {
            {{ "premium.manage.no-more-charge" | translate }}
          } @else if (isGifted && periodEndLabel) {
            {{ "premium.manage.until" | translate: { date: periodEndLabel } }}
          } @else if (periodEndLabel) {
            {{
              "premium.manage.next-renewal"
                | translate: { date: periodEndLabel }
            }}
          }
        </p>

        <!-- La barre dit d'un coup d'œil où l'on en est dans la période. Elle
             n'apparaît que si la durée est connue : un accès offert peut durer
             n'importe combien de temps. -->
        @if (periodProgress !== null) {
          <div
            class="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/20"
          >
            <div
              class="h-full rounded-full bg-gradient-to-r from-[#F0C97A] to-[#F79400]"
              [style.width.%]="periodProgress"
            ></div>
          </div>
        }

        @if (sinceLabel) {
          <p class="mt-2.5 text-[11px] text-white/60">
            {{
              "premium.manage.member-since" | translate: { date: sinceLabel }
            }}
          </p>
        }

        @if (state?.status === "PAST_DUE") {
          <div class="mt-3 rounded-xl bg-[#FFF4E5] px-3 py-2">
            <p
              class="text-[11px] font-bold uppercase tracking-wide text-[#B36A00]"
            >
              {{ "premium.manage.past-due-badge" | translate }}
            </p>
            <p class="mt-0.5 text-xs text-[#8A5A12]">
              {{ "premium.manage.past-due" | translate }}
            </p>
          </div>
        }
      </div>
    } @else {
      <div class="px-5 pb-2 pr-16 pt-6">
        <h2 class="text-2xl font-semibold text-[#062825]">
          {{ "premium.manage.title" | translate }}
        </h2>
      </div>
    }

    <div class="px-4 pt-4">
      @if (confirmingCancel) {
        <!-- Le message dit exactement jusqu'a quand l'acces reste ouvert -->
        <div class="rounded-2xl border border-[#E3E8E5] px-4 py-4">
          <p class="pb-1 text-base font-semibold text-[#062825]">
            {{ "premium.manage.cancel-confirm-title" | translate }}
          </p>
          <p class="pb-4 text-sm text-[#6E7B75]">{{ cancelConfirmMessage }}</p>
          <div class="flex gap-3">
            <vw-button
              look="clear"
              [label]="'premium.manage.cancel-yes' | translate"
              [loading]="loading"
              customClass="flex-1"
              (click)="confirmCancel()"
            />
            <vw-button
              look="primary"
              [label]="'premium.manage.cancel-no' | translate"
              customClass="flex-1"
              (click)="confirmingCancel = false"
            />
          </div>
        </div>
      } @else {
        <!-- Ce que l'échéance retire, concrètement. Un essai qui s'arrête sans
             qu'on ait jamais dit ce qu'il contenait ne se renouvelle pas : la
             liste donne les chiffres du gratuit, là où l'offre vante l'illimité. -->
        @if (showAfterSection) {
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-[#9BA6A1]"
          >
            {{
              "premium.manage.after.title" | translate: { date: periodEndLabel }
            }}
          </p>
          <ul class="mt-2 flex flex-col gap-2">
            <li class="flex items-start gap-2.5">
              <span
                class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D6B36A]"
              ></span>
              <span class="text-sm leading-snug text-[#062825]">
                {{
                  "premium.manage.after.willy"
                    | translate: { count: willyFreeLimit }
                }}
              </span>
            </li>
            <li class="flex items-start gap-2.5">
              <span
                class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D6B36A]"
              ></span>
              <span class="text-sm leading-snug text-[#062825]">
                {{ "premium.manage.after.lunar" | translate }}
              </span>
            </li>
            <li class="flex items-start gap-2.5">
              <span
                class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D6B36A]"
              ></span>
              <span class="text-sm leading-snug text-[#062825]">
                {{ "premium.manage.after.harvest" | translate }}
              </span>
            </li>
          </ul>
        }

        @if (isCanceled) {
          <vw-button
            look="primary"
            [label]="'premium.manage.reactivate' | translate"
            [loading]="loading"
            customClass="w-full mt-4"
            (click)="reactivate()"
          />
        }

        @if (isGifted) {
          <!-- Essai ou mois offerts : rien à annuler, l'échéance fait foi. Le
               libellé ne dit PAS « Passer Premium » : la personne l'est déjà.
               Ce qu'on lui propose, c'est de ne pas perdre l'accès. -->
          <vw-button
            look="primary"
            [label]="'premium.manage.keep-access' | translate"
            customClass="w-full mt-4"
            (click)="onClose({ upgrade: true })"
          />
          <p class="mt-1.5 text-center text-xs text-[#6E7B75]">
            {{
              "premium.manage.keep-access-hint"
                | translate: { date: periodEndLabel }
            }}
          </p>
        }

        <!-- Un mensuel n'avait aucun moyen de passer à l'annuel depuis cet
             écran : la seule porte était le paywall, que l'on masque justement
             aux abonnés. -->
        @if (canSwitchToYearly) {
          <button
            type="button"
            (click)="onClose({ upgrade: true })"
            class="mt-4 flex w-full items-center justify-between rounded-2xl border border-[#C3E5CE] bg-[#F7FCF9] px-4 py-3 text-left"
          >
            <span class="min-w-0 pr-2">
              <span class="block text-sm font-medium text-[#062825]">
                {{ "premium.manage.switch-yearly" | translate }}
              </span>
              <span class="block text-xs text-[#617069]">
                {{ "premium.paywall.yearly" | translate }} ·
                {{ "premium.paywall.yearly-badge" | translate }}
              </span>
            </span>
            <span class="shrink-0 text-lg leading-none text-[#317756]"
              >&rsaquo;</span
            >
          </button>
        }

        <!-- Abonnement du magasin : ni annulation ni facturation ici. La seule
             porte est celle du magasin, et « Restaurer » est exige par Apple. -->
        @if (isStore) {
          <p
            class="mt-4 rounded-2xl bg-[#F7FCF9] px-4 py-3 text-xs leading-relaxed text-[#1C5C41]"
          >
            {{ "premium.store.managed" | translate }}
          </p>
          <vw-button
            look="primary"
            [label]="manageInStoreKey | translate"
            customClass="w-full mt-3"
            (click)="openBilling()"
          />
          <button
            type="button"
            class="mt-3 w-full py-2 text-center text-xs font-medium text-[#1C5C41] underline disabled:opacity-50"
            [disabled]="restoring"
            (click)="restorePurchases()"
          >
            {{ "premium.store.restore" | translate }}
          </button>
          @if (restored === true) {
            <p class="mt-1 text-center text-xs text-[#1C5C41]">
              {{ "premium.store.restore-done" | translate }}
            </p>
          } @else if (restored === false) {
            <p class="mt-1 text-center text-xs text-[#6E7B75]">
              {{ "premium.store.restore-none" | translate }}
            </p>
          }
        }

        @if (isStripe) {
          <button
            type="button"
            (click)="openBilling()"
            class="mt-3 flex w-full items-center justify-between rounded-2xl border border-[#E3E8E5] px-4 py-3 text-left"
          >
            <span class="text-sm text-[#062825]">{{
              "premium.manage.billing" | translate
            }}</span>
            <svg
              class="h-4 w-4 text-[#9BA6A1]"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        }
      }

      @if (error) {
        <p class="mt-3 text-center text-sm text-[#E61A27]">
          {{ "common.error" | translate }}
        </p>
      }
    </div>

    <!-- La sortie, en bas et séparée : accessible en deux gestes, jamais au
         milieu du contenu où elle passait pour l'action principale. -->
    <!-- `!isStore` : un abonnement du magasin ne se resilie pas ici, et offrir
         le bouton reviendrait a promettre ce que le back refuse. -->
    @if (!confirmingCancel && state?.isPremium && !isCanceled && !isStore) {
      <div class="mt-auto border-t border-[#F0F2F1] px-4 pb-8 pt-3">
        <button
          type="button"
          (click)="confirmingCancel = true"
          class="w-full py-2 text-center text-sm text-[#8C9490]"
        >
          {{ cancelLabel | translate }}
        </button>
      </div>
    }
  </div>
</vw-toast-popup>
